/**
 * Authentication context.
 *
 * Same shape the Base44 version exposed - App.jsx and ProtectedRoute read
 * isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin,
 * isAuthenticated, authChecked and checkUserAuth, and none of them changed.
 * What changed is underneath: MSAL and Entra ID instead of a Base44 token in
 * the URL.
 *
 * `isLoadingPublicSettings` and `appPublicSettings` are kept because the app
 * renders on them. There is no public-settings endpoint any more, so the flag
 * simply settles immediately.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { base44, startDownloadSession } from '@/api/base44Client';
import { getAccount, initMsal, login, logout as msalLogout } from '@/lib/msal';
import { queryClientInstance } from '@/lib/query-client';
import { prefetchStartPage } from '@/lib/portfolioQueries';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [authError, setAuthError] = useState(null);

  const checkUserAuth = useCallback(async () => {
    setIsLoadingAuth(true);
    setAuthError(null);
    try {
      await initMsal();

      if (!getAccount()) {
        setIsAuthenticated(false);
        setUser(null);
        setAuthError({ type: 'auth_required', message: 'Authentication required' });
        return;
      }

      /* Opening on My Day or the Dashboard, its lists start now, alongside
         /me, rather than once the app has rendered on /me's answer. */
      prefetchStartPage(queryClientInstance, window.location.pathname);

      /* /api/me both identifies the caller and provisions their app_user row,
         so a first-time signer-in lands here rather than on an error. */
      const profile = await base44.auth.me();
      setUser(profile);
      setIsAuthenticated(true);

      /* The download cookie is only for older document links followed from
         an email or an exported PDF; the app's own links go through
         /OpenDocument and do not use it. Without it those older links are
         redirected to /OpenDocument anyway, so it never blocks sign-in. */
      startDownloadSession();
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);

      if (error?.status === 401) {
        setAuthError({ type: 'auth_required', message: 'Authentication required' });
      } else if (error?.status === 403) {
        /* The account is real but has been disabled, or the app registration
           has not been consented for them. */
        setAuthError({
          type: 'user_not_registered',
          message: error.message || 'This account does not have access to the Project Hub',
        });
      } else {
        setAuthError({
          type: 'unknown',
          message: error?.message || 'Could not sign you in. Please try again.',
        });
      }
    } finally {
      setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  const logout = (shouldRedirect = true) => {
    base44.auth.clearCache();
    setUser(null);
    setIsAuthenticated(false);
    if (shouldRedirect) msalLogout(window.location.origin);
  };

  const navigateToLogin = () => login(window.location.href);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        /* Retained for App.jsx; there are no remote public settings now. */
        isLoadingPublicSettings: false,
        appPublicSettings: null,
        authChecked,
        authError,
        logout,
        navigateToLogin,
        checkUserAuth,
        checkAppState: checkUserAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
